import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  notification as notificationIcon,
  PhoneCall as PhoneCallIcon,
  User as UserIcon,
} from '../../assets/icons';
import { useUpdateUserMutation } from '../../services/usergroups.api';
import { User } from '../../stores/types';
import { UserRole } from '../../stores/types/usergroups.interface';
import {
  Button,
  ButtonSize,
  ButtonType,
  CheckBoxData,
  ColorType,
  GroupDirection,
  Label,
  LabelType,
  RadioGroup,
  useGlobalModalContext,
} from '../_ui';
import { Form } from '../_ui/Form.component';

interface Props {
  user: User;
  adminUserGroupId: string;
}

export const EditPermissionModal = memo(({ user, adminUserGroupId }: Props) => {
  const { t } = useTranslation();
  const { hideModal } = useGlobalModalContext();
  const [updateUser, updateUserResult] = useUpdateUserMutation();
  const [selectedRole, setSelectedRole] = useState<UserRole>(user.userRole);

  const roleOptions = useMemo(() => {
    return [
      {
        id: UserRole.ADMIN,
        label: t('user_role_admin'),
        secondaryLabel: t('user_role_admin_description'),
        selected: selectedRole === UserRole.ADMIN,
      },
      {
        id: UserRole.READ_ONLY,
        label: t('user_role_read_only'),
        secondaryLabel: t('user_role_read_only_description'),
        selected: selectedRole === UserRole.READ_ONLY,
      },
      {
        id: UserRole.FACILITY_MANAGER,
        label: t('user_role_facility_manager'),
        secondaryLabel: t('user_role_facility_manager_description'),
        selected: selectedRole === UserRole.FACILITY_MANAGER,
      },
      {
        id: UserRole.ACCOUNTING,
        label: t('user_role_accounting'),
        secondaryLabel: t('user_role_accounting_description'),
        selected: selectedRole === UserRole.ACCOUNTING,
      },
      {
        id: UserRole.TECHNICIAN,
        label: t('user_role_technician'),
        secondaryLabel: t('user_role_technician_description'),
        selected: selectedRole === UserRole.TECHNICIAN,
      },
    ];
  }, [selectedRole, t]);

  const handleRoleChange = (items: CheckBoxData[]) => {
    const selectedOption = items.find((item) => item.selected === true);
    if (selectedOption) {
      setSelectedRole(selectedOption.id as UserRole);
    }
  };

  const handleSave = () => {
    updateUser({
      email: user.email,
      adminUserGroupId: adminUserGroupId,
      userRole: selectedRole,
    });
  };

  return (
    <Form
      onSubmit={handleSave}
      queryResult={updateUserResult}
      onQuerySuccess={hideModal}
      onQueryFailed={hideModal}
    >
      <div className='flex flex-col gap-[16px]'>
        {/* Team Member Info Section */}
        <div className='flex flex-col gap-[12px]'>
          <Label text={t('team_member_info')} type={LabelType.H5} color={ColorType.BLACK} />
          <div className='text-sm font-normal font-inter text-grey5'>
            <Label
              icon={UserIcon}
              text={user.name}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
            <Label
              icon={PhoneCallIcon}
              text={user.phone}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
            <Label
              icon={notificationIcon}
              text={user.email}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          </div>
        </div>
        <div className='flex flex-col gap-[12px]'>
          <Label text={t('company_role_title')} type={LabelType.H5} color={ColorType.BLACK} />
          <Label
            htmlText={t('company_role_description')}
            type={LabelType.BODY4}
            color={ColorType.GREY5}
          />
          <RadioGroup
            direction={GroupDirection.Vertical}
            defaultItems={roleOptions}
            labelType={LabelType.LABEL_S_MEDIUM}
            secondaryLabelType={LabelType.BODY3}
            padding='mt-4'
            itemHeight='52px'
            onChange={handleRoleChange}
          />
        </div>

        {/* Action Buttons */}
        <div className='flex flex-col pt-[16px] pb-[20px] sticky bottom-0 bg-white'>
          <Button
            label={t('save')}
            type={ButtonType.PRIMARY}
            size={ButtonSize.SMALL_FULL}
            onClick={handleSave}
            dataTestId='sendInviteButton'
          />
        </div>
      </div>
    </Form>
  );
});

EditPermissionModal.displayName = 'EditPermissionModal';
